import dayjs from 'dayjs';
import moment from 'moment-timezone';

import { RootState } from '../../../app/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { setTotalDaysOfWeek } from './helpers';

const initialState = {
    mode: 'day',
    schedules: [],
    isLoading: false,
    hoveredDay: null,
    currentTime: new Date()?.toISOString(),
    selectedDate: dayjs().startOf('date').toISOString(),
    selectedWeekDays: [],
    isGroupingEnabled: false,
    externalClickSignal: 0,
    selectedDeviceType: null,
    triggeredEvent: null,
};

const scheduleSlice = createSlice({
    name: 'schedule',
    initialState: initialState,
    reducers: {
        resetSchedule: () => {
            return initialState;
        },
        triggerExternalClick: (state, action) => {
            state.externalClickSignal = Date.now(); // unique value to trigger useEffect
            state.triggeredEvent = action.payload;
        },
        resetExternalClick: (state) => {
            state.externalClickSignal = 0;
            state.triggeredEvent = null;
        },
        setPredefinedScheduleConfiguration: (state: RootState, action: PayloadAction<any>) => {
            Object.keys(action.payload)?.forEach((key: string) => {
                if (key !== 'filters') {
                    state[key] = action.payload[key];
                }
            });
        },
        toggleScheduleViewMode: (state: RootState, action: PayloadAction<any>) => {
            const { mode, startOfWeek = 'sunday', timezone = 'UTC' } = action.payload;
            if (mode === 'week') {
                const now = moment(Date.now()).tz(timezone);
                const targetStartDay = startOfWeek === 'monday' ? 'isoWeek' : 'week';
                const startOfWeekDate = now.clone().startOf(targetStartDay);
                state.selectedDate = startOfWeekDate.toISOString();
                state.selectedWeekDays = setTotalDaysOfWeek(startOfWeekDate.toISOString());
            } else {
                const dateStr = moment.tz(timezone).startOf('day').format('MM/DD/YYYY');
                state.selectedDate = moment(dateStr, 'MM/DD/YYYY').utc().toISOString();
            }
            state.mode = mode;
        },
        setSelectedScheduleFilterDate: (state: RootState, action: PayloadAction<any>) => {
            state['selectedDate'] = action.payload;
            let days: any = [];
            if (state.mode === 'week') {
                days = setTotalDaysOfWeek(action.payload);
            }
            state['selectedWeekDays'] = days;
        },
        setHoveredScheduleFilterDate: (state: RootState, action: PayloadAction<any>) => {
            state.hoveredDay = action.payload;
        },
        setInit: (state: RootState, action: PayloadAction<any>) => {
            const { timezone = 'UTC' } = action.payload;
            const dateStr = moment.tz(timezone).startOf('day').format('MM/DD/YYYY');
            state.selectedDate = moment(dateStr, 'MM/DD/YYYY').utc().toISOString();
        },
        toggleScheduleGrouping: (state: RootState) => {
            state.isGroupingEnabled = !state.isGroupingEnabled;
        },
        toggleScheduleComponentLoader: (state: RootState, action: PayloadAction<any>) => {
            state.isLoading = action.payload;
        },
        setSchedulesForComponent: (state: RootState, action: PayloadAction<any>) => {
            state['schedules'] = action.payload;
        },
        setCurrentTime: (state: RootState, action: PayloadAction<string>) => {
            state['currentTime'] = action.payload;
        },
        setComponentSelectedDeviceType: (state: RootState, action: PayloadAction<any>) => {
            state['selectedDeviceType'] = action.payload;
        },
    },
});

export const {
    resetSchedule,
    triggerExternalClick,
    setCurrentTime,
    toggleScheduleViewMode,
    setPredefinedScheduleConfiguration,
    toggleScheduleGrouping,
    setSchedulesForComponent,
    setHoveredScheduleFilterDate,
    setSelectedScheduleFilterDate,
    toggleScheduleComponentLoader,
    setComponentSelectedDeviceType,
    setInit,
    resetExternalClick,
} = scheduleSlice.actions;

export default scheduleSlice;
